import React, { useEffect, useState, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import PageHeader from '../../components/ui/PageHeader';
import ActionToolbar from '../../components/ui/ActionToolbar';
import DialogHeader from '../../components/ui/DialogHeader';
import { DevolucionService } from '../../services/DevolucionService';
import { VentaService } from '../../services/VentaService';
import { useAuth } from '../../context/AuthContext';

const Devoluciones = () => {
    const { user } = useAuth();
    const toast = useRef(null);
    const [devoluciones, setDevoluciones] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Estados de dialogo
    const [devolucionDialog, setDevolucionDialog] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    
    // Entidad
    const emptyDevolucion = {
        idVenta: null,
        motivo: '',
        montoReembolsado: 0,
        estado: 'procesada',
        idEmpresa: user?.idEmpresa || 1,
        idSucursal: user?.idSucursal || 1
    };
    const [devolucion, setDevolucion] = useState(emptyDevolucion);
    const [ventaSeleccionada, setVentaSeleccionada] = useState(null);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await DevolucionService.getAll(user?.idEmpresa || 1);
            setDevoluciones(data || []);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las devoluciones', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const openNew = () => {
        setDevolucion(emptyDevolucion);
        setVentaSeleccionada(null);
        setSubmitted(false);
        setDevolucionDialog(true);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setDevolucionDialog(false);
    };

    const buscarVenta = async (e) => {
        if (e.key === 'Enter') {
            const codigoVenta = e.target.value;
            try {
               // Buscamos si el API de venta trae busqueda por codigo, asumimos getAll por ahora y loop 
               // (Idealmente deberíamos usar VentaService.getByCodigo, en su defecto getAll filtrado)
               const res = await VentaService.getAll();
               const ventaFound = (res?.data || []).find(v => v.codigoVenta === codigoVenta || v.id.toString() === codigoVenta);
               
               if (ventaFound) {
                   setVentaSeleccionada(ventaFound);
                   setDevolucion({...devolucion, idVenta: ventaFound.id, montoReembolsado: ventaFound.montoTotal || ventaFound.total || 0});
                   toast.current.show({ severity: 'info', summary: 'Venta Encontrada', detail: 'Se pre-cargó el monto de la venta.', life: 3000 });
               } else {
                   setVentaSeleccionada(null);
                   toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'No se encontró la venta.', life: 3000 });
               }
            } catch (err) {
                 toast.current.show({ severity: 'error', summary: 'Error', detail: 'Fallo al buscar venta.', life: 3000 });
            }
        }
    };

    const saveDevolucion = async () => {
        setSubmitted(true);
        if (devolucion.idVenta && devolucion.motivo.trim() && devolucion.montoReembolsado >= 0) {
            try {
                await DevolucionService.create(devolucion);
                toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Devolución Procesada Exitosamente', life: 3000 });
                setDevolucionDialog(false);
                loadData();
            } catch (error) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo crear la devolución.', life: 3000 });
            }
        }
    };

    const dineroBody = (monto) => `S/ ${Number(monto || 0).toFixed(2)}`;

    return (
        <div className="grid">
            <div className="col-12">
                <PageHeader 
                    title="Devoluciones" 
                    description="Registre y consulte las devoluciones de productos realizadas." 
                    icon="pi pi-replay" 
                />
                <div className="card shadow-2 border-round-xl">
                    <Toast ref={toast} />
                    
                    <ActionToolbar
                        onNew={openNew}
                        newLabel="Registrar Devolución"
                        onSearch={(val) => setGlobalFilter(val)}
                        searchValue={globalFilter}
                        searchPlaceholder="Buscar devoluciones..."
                    />

                    <DataTable value={devoluciones} loading={loading} paginator rows={10} globalFilter={globalFilter} emptyMessage="No hay devoluciones registradas.">
                        <Column field="fechaProceso" header="Fecha de Proceso" body={(row) => row.fechaProceso ? new Date(row.fechaProceso).toLocaleString() : '-'} sortable />
                        <Column field="idVenta" header="ID Venta Orig." sortable />
                        <Column field="motivo" header="Motivo" sortable />
                        <Column field="montoReembolsado" header="Costo de Reembolso" body={(row) => dineroBody(row.montoReembolsado)} sortable />
                        <Column field="estado" header="Estado" body={(row) => row.estado ? row.estado.toUpperCase() : 'PROCESADA'} sortable />
                    </DataTable>

                    {/* Dialog Creacion */}
                    <Dialog visible={devolucionDialog} style={{ width: '450px' }} header={
                        <DialogHeader
                            title="Registrar Devolución"
                            subtitle="Procese una devolución vinculada a una venta"
                            icon="pi pi-replay"
                        />
                    } modal className="p-fluid custom-profile-dialog" footer={(
                        <>
                            <Button label="Cancelar" icon="pi pi-times" outlined onClick={hideDialog} />
                            <Button label="Guardar y Procesar" icon="pi pi-check"  onClick={saveDevolucion} />
                        </>
                    )} onHide={hideDialog}>
                        <div className="field mb-4">
                            <label htmlFor="idVenta" className="font-bold">Código o ID de Venta (Presione Enter para buscar)</label>
                            <InputText id="idVenta" placeholder="Ej: V-001 o 5" onKeyDown={buscarVenta} className={submitted && !devolucion.idVenta ? 'p-invalid w-full' : 'w-full'} autoFocus />
                            {submitted && !devolucion.idVenta && <small className="p-error">El ID de Venta es requerido.</small>}
                            {ventaSeleccionada && <small className="block text-green-600 mt-2 font-bold">Venta enlazada exitosamente (Total: {dineroBody(ventaSeleccionada.montoTotal || ventaSeleccionada.total)})</small>}
                        </div>
                        
                        <div className="field mb-4">
                            <label htmlFor="montoReembolsado" className="font-bold">Monto a Reembolsar S/</label>
                            <InputNumber id="montoReembolsado" value={devolucion.montoReembolsado} onValueChange={(e) => setDevolucion({...devolucion, montoReembolsado: e.value})} mode="currency" currency="PEN" locale="es-PE" className="w-full" disabled={!ventaSeleccionada} />
                            {submitted && devolucion.montoReembolsado == null && <small className="p-error">El monto es requerido.</small>}
                        </div>

                        <div className="field">
                            <label htmlFor="motivo" className="font-bold">Justificación / Motivo</label>
                            <InputTextarea id="motivo" value={devolucion.motivo} onChange={(e) => setDevolucion({...devolucion, motivo: e.target.value})} rows={3} className={submitted && !devolucion.motivo.trim() ? 'p-invalid w-full' : 'w-full'} />
                            {submitted && !devolucion.motivo.trim() && <small className="p-error">La justificación es requerida.</small>}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default Devoluciones;
